import { useEffect, useState } from "react";
import { Loader2, Mail, Send, History } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const fmtDt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const COLOR = { ok: "#10B981", warn: "#F59E0B", ko: "#EF4444" };
const LABEL = { ok: "tutto regolare", warn: "attenzione", ko: "problema" };

export function SendSheetDialog({ open, onClose, defaultEmail, workspaceName }) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTo(defaultEmail || ""); setSubject(`Scheda tecnica integrazione WhatsApp Business Platform — ${workspaceName || ""}`.trim()); setMessage(""); } }, [open, defaultEmail, workspaceName]);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/integration-info/send", { to, subject, message });
      toast.success(`Scheda inviata a ${data.to}`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#111827] border-[#1F2937] text-slate-100 max-w-md" data-testid="send-sheet-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-slate-100"><Mail className="h-4 w-4 text-[#10B981]" /> Invia scheda tecnica via email</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400 -mt-2">Il PDF viene generato al momento con i dati attuali e allegato all'email. Il token non è incluso.</p>
        <div className="space-y-2">
          <div><label className="text-[11px] text-slate-400">Destinatario</label><Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="nome@azienda.it" data-testid="send-sheet-to" className="bg-[#0A0E17] border-[#1F2937] text-slate-100" /></div>
          <div><label className="text-[11px] text-slate-400">Oggetto</label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="send-sheet-subject" className="bg-[#0A0E17] border-[#1F2937] text-slate-100" /></div>
          <div><label className="text-[11px] text-slate-400">Messaggio (facoltativo)</label><Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Breve testo di accompagnamento…" data-testid="send-sheet-message" className="bg-[#0A0E17] border-[#1F2937] text-slate-100" /></div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="text-slate-300" data-testid="send-sheet-cancel">Annulla</Button>
          <Button onClick={send} disabled={busy || !to} data-testid="send-sheet-submit" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Invia
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TokenCheckHistory({ refreshKey }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (open) api.get("/whatsapp/token-checks").then((r) => setD(r.data)).catch(() => setD({ timeline: [], changes: [], recent: [], total: 0 })); }, [open, refreshKey]);
  return (
    <div className="space-y-2" data-testid="token-history">
      <button type="button" onClick={() => setOpen(!open)} className="text-[11px] text-slate-400 hover:text-slate-100 inline-flex items-center gap-1" data-testid="token-history-toggle">
        <History className="h-3 w-3" /> {open ? "Nascondi" : "Mostra"} storico controlli
      </button>
      {open && (!d ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" /> : (
        <div className="space-y-3">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Ultimi 60 giorni</p>
            <div className="flex gap-[2px] h-6 items-end" data-testid="token-history-timeline">
              {d.timeline.map((t) => (
                <div key={t.date} title={`${t.date.split("-").reverse().join("/")}: ${t.level ? LABEL[t.level] : "nessun controllo"}`}
                     className="flex-1 rounded-sm" style={{ height: t.level ? "100%" : "35%", backgroundColor: t.level ? COLOR[t.level] : "#1F2937" }} />
              ))}
            </div>
            <div className="flex justify-between text-[9px] text-slate-500 mt-0.5"><span>{d.timeline[0]?.date.split("-").reverse().slice(0, 2).join("/")}</span><span>oggi</span></div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Cambi di stato</p>
            {!d.changes.length ? <p className="text-xs text-slate-500" data-testid="token-history-no-changes">Nessun cambio di stato registrato.</p> : (
              <ul className="space-y-1" data-testid="token-history-changes">
                {d.changes.map((c) => (
                  <li key={c.id} className="text-xs flex gap-2 items-start">
                    <span className="h-2 w-2 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: COLOR[c.level] }} />
                    <span className="text-slate-300">{fmtDt(c.checked_at)} · <span style={{ color: COLOR[c.level] }}>{LABEL[c.level]}</span>{c.issues?.length ? <span className="text-slate-500"> — {c.issues.join(" · ")}</span> : null}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Ultimi 10 controlli ({d.total} negli ultimi 60 giorni)</p>
            <div className="flex flex-wrap gap-1" data-testid="token-history-recent">
              {d.recent.map((r) => (
                <span key={r.id} className="text-[10px] rounded-full px-2 py-0.5 border" style={{ borderColor: COLOR[r.level], color: COLOR[r.level] }} title={r.issues?.join(" · ")}>{fmtDt(r.checked_at)}</span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
